import { Box, Button, Card, CardHeader, Container, Divider, Grid, Stack, TextField, Typography } from "@mui/material";
import { Budget } from "../../components/dashboard/budget";
import { TasksProgress } from "../../components/dashboard/tasks-progress";
import { TotalCustomers } from "../../components/dashboard/total-customers";
import { TotalProfit } from "../../components/dashboard/total-profit";
import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../theme";
import { useEffect, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import NewSalesChart from "./NewSalesChart";

axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("user");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

function Dashboard() {
    const [data, setData] = useState(null);
    const [isLoading, setLoading] = useState(true);

    // 🔥 MẶC ĐỊNH KHI VÀO: Chỉ lấy dữ liệu hôm nay
    const [dateRange, setDateRange] = useState([new Date(), new Date()]);
    const [startDate, endDate] = dateRange;

    const fetchStatistic = async (start, end) => {
        setLoading(true);
        try {
            // Chuẩn hóa ngày để lấy trọn vẹn từ 00:00:00 đến 23:59:59
            let s = null;
            let e = null;

            if (start) {
                s = new Date(start);
                s.setHours(0, 0, 0, 0);
            }
            if (end) {
                e = new Date(end);
                e.setHours(23, 59, 59, 999);
            }

            const params = {
                dateStart: s ? s.toISOString() : null,
                dateEnd: e ? e.toISOString() : null
            };

            const res = await axios.get(`${process.env.REACT_APP_API_URL}/statistic/getalladmin`, { params });

            if (res.data.success) {
                setData(res.data.data);
            }
            setLoading(false);
        } catch (err) {
            console.error("Lỗi API thống kê:", err);
            setLoading(false);
        }
    };

    useEffect(() => {
        // Logic: Khi có start và end (hoặc cả 2 cùng trống) thì mới fetch
        if ((startDate && endDate) || (!startDate && !endDate)) {
            fetchStatistic(startDate, endDate);
        }
    }, [startDate, endDate]);

    const handleSetToday = () => {
        const today = new Date();
        setDateRange([today, today]);
    };

    const handleSetThisMonth = () => {
        const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        setDateRange([firstDay, new Date()]);
    };

    const handleSetAllTime = () => {
        setDateRange([null, null]); 
    };

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
                    {isLoading && (
                        <div className="loading" style={{ maxWidth: "100%", zIndex: 9999 }}>
                            <div className="loader"></div>
                        </div>
                    )}

                    <Container maxWidth={false}>
                        <Card sx={{ mb: 4, p: 2, border: "1px solid #E6E8F0" }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                                <Typography variant="subtitle2" sx={{ color: "#65748B", minWidth: 100 }}>
                                    Lọc theo ngày:
                                </Typography>

                                <DatePicker
                                    selectsRange={true}
                                    startDate={startDate}
                                    endDate={endDate}
                                    onChange={(update) => setDateRange(update)}
                                    maxDate={new Date()}
                                    isClearable={true}
                                    placeholderText="Toàn thời gian"
                                    dateFormat="dd/MM/yyyy"
                                    customInput={<TextField size="small" sx={{ width: 280 }} />}
                                />

                                <Stack direction="row" spacing={1}>
                                    <Button variant={startDate && endDate && startDate.toDateString() === new Date().toDateString() ? "contained" : "outlined"} 
                                            size="small" onClick={handleSetToday}>
                                        Hôm nay
                                    </Button>
                                    <Button variant="outlined" size="small" onClick={handleSetThisMonth}>
                                        Tháng này
                                    </Button>
                                    <Button variant="contained" color="secondary" size="small" onClick={handleSetAllTime}>
                                        Tất cả
                                    </Button>
                                </Stack>
                            </Stack>
                        </Card>

                        <Grid container spacing={3}>
                            <Grid item xl={3} lg={6} sm={6} xs={12}>
                                {data && <TotalCustomers user={data.thanhvien} />}
                            </Grid>
                            <Grid item xl={3} lg={6} sm={6} xs={12}>
                                {data && <Budget tongnap={data.tongnaps} />}
                            </Grid>
                            <Grid item xl={3} lg={6} sm={6} xs={12}>
                                {data && <TasksProgress tongrut={data.tongruts} />}
                            </Grid>
                            <Grid item xl={3} lg={6} sm={6} xs={12}>
                                {data && <TotalProfit tongcuoc={data.tongcuocminigame} sx={{ height: "100%" }} />}
                            </Grid>

                            <Grid item lg={12} md={12} xl={12} xs={12}>
                                <Card sx={{ border: "1px solid #E6E8F0", borderRadius: 2 }}>
                                    <CardHeader
                                        title={
                                            startDate && endDate
                                                ? `Báo cáo: ${startDate.toLocaleDateString("vi-VN")} - ${endDate.toLocaleDateString("vi-VN")}`
                                                : "Báo cáo: TOÀN THỜI GIAN"
                                        }
                                        titleTypographyProps={{ variant: "h6" }}
                                    />
                                    <Divider />
                                    <Box sx={{ p: 3, height: 500, position: "relative" }}>
                                        {data ? (
                                            <NewSalesChart data={data} />
                                        ) : (
                                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                                Chưa có dữ liệu thống kê...
                                            </Box>
                                        )}
                                    </Box>
                                </Card>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default Dashboard;